import { useState } from "react";
import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { getRequestWithToken, postRequest } from "../../utils/ApiRequests";

const example = (entityType) => ({ schemaVersion: 1, entityType, records: [{ id: "66281085434eb0a32cb01a0b", name: "Reference only", sourceEnglish: {}, translations: { hn: {}, hi: {}, bn: {}, gu: {}, kn: {}, mr: {}, ta: {}, te: {} } }] });

const download = (value, name) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

export default function SeoTranslationImport({ entityTypes = ["sector", "occupation", "jobRole"] , onApplied = () => {} }) {
  const [entityType, setEntityType] = useState(entityTypes[0]);
  const [document, setDocument] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const validate = async (dryRun) => {
    setBusy(true);
    setError("");
    setValidationErrors([]);
    try {
      const response = await postRequest("admin/seo/translations/import", { document, dryRun });
      setSummary(response.data.data);
      if (!dryRun) onApplied();
    } catch (requestError) {
      setSummary(null);
      setValidationErrors(requestError?.response?.data?.errors || []);
      setError(requestError?.response?.data?.msg || requestError?.response?.data?.message || requestError.message);
    } finally { setBusy(false); }
  };

  const selectFile = async (event) => {
    setSummary(null);
    setError("");
    setValidationErrors([]);
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return setError("File must be 5 MB or smaller.");
    try {
      const parsed = JSON.parse(await file.text());
      if (parsed.entityType !== entityType) throw new Error(`Choose ${parsed.entityType} before validating this file.`);
      setDocument(parsed);
    } catch (parseError) { setDocument(null); setError(parseError.message); }
  };

  return <Box sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
    <Typography variant="h6" mb={1}>Translation JSON import</Typography>
    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <TextField select size="small" label="Record type" value={entityType} onChange={(event) => { setEntityType(event.target.value); setDocument(null); setSummary(null); }}>
        {entityTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
      </TextField>
      <Button component="label" variant="outlined">Choose JSON<input hidden type="file" accept="application/json,.json" onChange={selectFile} /></Button>
      <Button onClick={() => download(example(entityType), `${entityType}-translation-example.json`)}>Download example JSON</Button>
      <Button variant="contained" disabled={!document || busy} onClick={() => validate(true)}>Validate / dry run</Button>
      <Button color="success" variant="contained" disabled={!summary || busy} onClick={() => window.confirm("Apply these translation updates?") && validate(false)}>Apply</Button>
    </Box>
    <Typography variant="caption">One file may contain up to 5,000 records and all eight translated languages. Blank fields remain unchanged.</Typography>
    {summary && <Alert severity="success" sx={{ mt: 1 }}>Matched {summary.matched}; records to update {summary.wouldUpdate}; unchanged {summary.unchanged}; fields {summary.fieldsUpdated}.</Alert>}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {validationErrors.map((item) => <Alert key={`${item.index}-${item.message}`} severity="error" sx={{ mt: 1 }}>Record {item.index + 1}{item.id ? ` (${item.id})` : ""}: {item.message}</Alert>)}
  </Box>;
}

export function SeoTranslationExport({ entityType, id }) {
  const exportJson = async () => {
    const response = await getRequestWithToken(`admin/seo/translations/export?entityType=${entityType}&ids=${id}`);
    download(response.data.data, `${entityType}-${id}-translations.json`);
  };
  return <Button variant="outlined" disabled={!id} onClick={exportJson}>Export Translation JSON</Button>;
}
